import { useState } from 'react'
import { StatusPill } from '@/components/ui'
import type { Run } from '@/types/database'
import { ChevronDown, Check, Package } from 'lucide-react'
import clsx from 'clsx'
import { useAdvisorShown } from '@/lib/useAdvisorShown'

// Runs below this read amber (mirrors runs.packout_percent semantics, mig 639).
const PACKOUT_AMBER_BELOW = 85

// Status badge used wherever a run status is shown (the shared status pill;
// `size` is kept for the callers that still pass it).
export function RunStatusBadge({ status }: { status: string; size?: 'xs' | 'sm' }) {
  return <StatusPill object="run" status={status} />
}

interface Props {
  /** All runs eligible for this page (already filtered by status) */
  runs:        Run[]
  /** The currently-selected run */
  selected:    Run | null
  /** Callback when the user picks a different run */
  onSelect:    (runId: string) => void
  /** Message shown when `runs` is empty — varies by page (tipping vs packing) */
  emptyLabel?: string
  /**
   * The run list never loaded. `runs` is then [] for a reason that has nothing
   * to do with the floor, and `emptyLabel` ("No runs available") would state it
   * as one. Set this and the picker says so instead.
   */
  unread?: boolean
}

// fallow-ignore-next-line complexity
export default function RunSelector({ runs, selected, onSelect, emptyLabel = 'No runs available', unread }: Props) {
  const [open, setOpen] = useState(false)

  // DSS P0 (spec §6): the packout amber below is an advisor already in
  // production — measure its exposures so P1 advisors land on a baseline.
  // Logging only; visuals unchanged.
  useAdvisorShown({
    advisorKey: 'run_packout_band',
    entityType: 'run',
    entityId:   selected?.run_id ?? null,
    recommendation: {
      verdict: selected?.packout_percent == null ? 'unknown'
        : selected.packout_percent < PACKOUT_AMBER_BELOW ? 'caution' : 'go',
      reasons: selected?.packout_percent != null
        ? [`Packout ${selected.packout_percent.toFixed(1)}% (amber below ${PACKOUT_AMBER_BELOW}%)`]
        : [],
      evidence: { packout_percent: selected?.packout_percent ?? null, amber_below: PACKOUT_AMBER_BELOW },
    },
    enabled: selected?.packout_percent != null,
  })

  if (runs.length === 0) {
    return (
      <div className="bg-yellow-900/40 border-b border-yellow-800 px-4 py-3">
        <p className="text-sm text-yellow-300 font-semibold flex items-center gap-2">
          <Package size={14} />
          {unread
            ? "Couldn't load the runs — this is a failed read, not an idle floor. Reload."
            : emptyLabel}
        </p>
      </div>
    )
  }

  // Row renderer for the open dropdown list.
  // fallow-ignore-next-line complexity
  const renderRun = (r: Run) => {
    const isSelected = r.run_id === selected?.run_id
    return (
      <button
        key={r.run_id}
        onClick={() => { onSelect(r.run_id); setOpen(false) }}
        className={clsx(
          'w-full flex items-start gap-3 px-4 py-3 border-b hairline last:border-0 text-left transition-colors',
          isSelected ? 'bg-green-900/20 hover:bg-green-900/30' : 'hover:bg-surface'
        )}
      >
        <div className={clsx('w-5 h-5 rounded-full border-2 shrink-0 mt-0.5 flex items-center justify-center',
          isSelected ? 'border-green-500 bg-green-500' : 'border-line-strong'
        )}>
          {isSelected && <Check size={12} className="text-white" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-fg">{r.run_number}</p>
            <RunStatusBadge status={r.status} size="xs" />
          </div>
          <p className="text-xs text-fg-muted truncate">
            {r.grower} · {r.variety}
          </p>
          <div className="flex gap-3 mt-0.5 text-[11px] text-fg-muted">
            <span>{r.tipped_net_weight_kg.toFixed(0)} kg tipped</span>
            <span>{r.pallet_count} pallets</span>
            {r.packout_percent != null && (
              <span className={clsx(
                r.packout_percent < PACKOUT_AMBER_BELOW ? 'text-amber-400' : 'text-green-400'
              )}>
                {r.packout_percent.toFixed(1)}% packout
              </span>
            )}
          </div>
        </div>
      </button>
    )
  }

  return (
    <div className="border-b hairline bg-canvas/80 backdrop-blur">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-surface/50 transition-colors"
      >
        <div className="flex-1 min-w-0 text-left">
          {selected ? (
            <>
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-fg truncate">
                  {selected.run_number}
                </p>
                <RunStatusBadge status={selected.status} size="xs" />
                {runs.length > 1 && (
                  <span className="text-xs surface text-fg-muted rounded-full px-1.5 py-0.5">
                    1 of {runs.length}
                  </span>
                )}
              </div>
              <p className="text-xs text-fg-muted truncate">
                {selected.grower} · {selected.variety}
                {selected.packout_percent != null && (
                  <span className={clsx(
                    'ml-2 font-semibold',
                    selected.packout_percent < PACKOUT_AMBER_BELOW ? 'text-amber-400' : 'text-green-400'
                  )}>
                    {selected.packout_percent.toFixed(1)}%
                  </span>
                )}
              </p>
            </>
          ) : (
            <p className="text-sm text-fg-muted">Select a run…</p>
          )}
        </div>
        <ChevronDown
          size={18}
          className={clsx('text-fg-muted shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="max-h-[50dvh] overflow-y-auto border-t hairline surface-sunk">
          {runs.map(renderRun)}
        </div>
      )}
    </div>
  )
}
